// --- CHANGE HERE: Import axios directly in this file ---
import axios from 'axios';
import api from './api';
import { API_ENDPOINTS, API_BASE_URL } from '../config/api';
import { trackAuthEvent } from '../utils/postHogUtils';

const authApi = axios.create({
  baseURL: API_BASE_URL,
});

export const setTokens = (accessToken, refreshToken) => {
  if (accessToken) {
    document.cookie = `accessToken=${accessToken}; path=/; max-age=900; secure;  samesite=strict`;
  }
  if (refreshToken) {
    document.cookie = `refreshToken=${refreshToken}; path=/; max-age=604800; secure; samesite=strict`; // 7 days
  }
};

// Removed setUser as it will be handled by Redux actions directly
// export const setUser = (user) => {
//   if (user) {
//     store.dispatch(setUserDetails(user));
//   }
// };

const clearTokens = () => {
  document.cookie = 'accessToken=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  document.cookie = 'refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
 };

const getRefreshToken = () => {
  const allCookies = document.cookie.split('; ');
  const tokenRow = allCookies.find(row => row.startsWith('refreshToken='));
  if (!tokenRow) {
    console.error('getRefreshToken: DID NOT FIND THE REFRESH TOKEN ROW. Returning null.');
    return null;
  }

  const token = tokenRow.split('=')[1];
  
  return token || null;
};

const getTokenFromCookie = () => {
  const token = document.cookie.split('; ').find(row => row.startsWith('accessToken='))?.split('=')[1];
  return token || null; 
};

// Function to fetch user details from API
const getUserDetails = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.AUTH.GET_USER_DETAILS);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch user details from API:', error);
    throw error;
  }
};

// Modified getUserFromStorage to only fetch from API
const getUserFromStorage = async () => {
  try {
    const user = await getUserDetails();
    return user;
  } catch (e) {
    console.error('Error getting user from API:', e);
    return null;
  }
};

const login = async (credentials) => {
  try {
    const data = await api.post(API_ENDPOINTS.AUTH.LOGIN, credentials);
    if (data && data.accessToken && data.refreshToken) {
      setTokens(data.accessToken, data.refreshToken);
      
      // Track successful login with error handling
      try {
        trackAuthEvent('user_logged_in', {
          method: 'email_password',
          user_id: data.user?._id || data.user?.id,
          email: data.user?.email,
          login_type: 'standard',
        });
      } catch (trackingError) {
        console.error('Failed to track login event:', trackingError);
      }
    }
    return data;
  } catch (error) {
    // Track failed login with error handling
    try {
      trackAuthEvent('login_failed', {
        error: error.message || 'Unknown error',
        email: credentials.email,
        error_code: error.response?.status,
      });
    } catch (trackingError) {
      console.error('Failed to track login failure:', trackingError);
    }
    throw error;
  }
};

const register = async (userData) => {
  try {
    const data = await api.post(API_ENDPOINTS.AUTH.REGISTER, userData);
    if (data && data.accessToken && data.refreshToken) {
      setTokens(data.accessToken, data.refreshToken);
      
      // Track successful registration with error handling
      try {
        trackAuthEvent('user_registered', {
          method: 'email_password',
          user_id: data.user?._id || data.user?.id,
          email: data.user?.email,
          registration_type: userData.registrationType || 'standard',
        });
      } catch (trackingError) {
        console.error('Failed to track registration event:', trackingError);
      }
    }
    // Removed direct setUser call, registerSuccess/loginSuccess actions handle this
    // if (data && data.user) setUser(data.user); 
    return data;
  } catch (error) {
    // Track failed registration with error handling
    try {
      trackAuthEvent('registration_failed', {
        error: error.message || 'Unknown error',
        email: userData.email,
        registration_type: userData.registrationType || 'standard',
        error_code: error.response?.status,
      });
    } catch (trackingError) {
      console.error('Failed to track registration failure:', trackingError);
    }
    throw error;
  }
};

const logout = async () => {
  try {
    // Track logout event before clearing tokens with error handling
    try {
      trackAuthEvent('user_logged_out', {
        logout_type: 'manual',
      });
    } catch (trackingError) {
      console.error('Failed to track logout event:', trackingError);
    }
    
    try {
      await api.logout();
      console.log('Backend logout successful');
    } catch (backendError) {
      console.error('Backend logout failed:', backendError);
    }
    
    clearTokens();
    return true;
  } catch (error) {
    console.error('Logout error:', error);
    clearTokens(); 
    return true;
  }
};

const refreshToken = async () => { 
  const currentRefreshToken = getRefreshToken();
  
  if (!currentRefreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await authApi.post(API_ENDPOINTS.AUTH.REFRESH_TOKEN, {
      refreshToken: currentRefreshToken
    });
    
    const { accessToken } = response.data;
    
    if (accessToken) {
      setTokens(accessToken, undefined);
      
      // Track successful token refresh with error handling
      try {
        trackAuthEvent('token_refreshed', {
          success: true,
        });
      } catch (trackingError) {
        console.error('Failed to track token refresh:', trackingError);
      }
      
      return accessToken;
    }
    
    throw new Error('Failed to refresh token: Access token not received.');
  } catch (error) {
    console.error("Refresh token failed, logging out:", error.response?.data?.message || error.message);
    
    // Track failed token refresh with error handling
    try {
      trackAuthEvent('token_refresh_failed', {
        error: error.message || 'Unknown error',
        error_code: error.response?.status,
      });
    } catch (trackingError) {
      console.error('Failed to track token refresh failure:', trackingError);
    }
    
     throw error;
  }
};

const isAuthenticated = () => {
  return !!getTokenFromCookie();
};

const checkExistingSession = async (email) => {
  try {
    const response = await api.post(API_ENDPOINTS.AUTH.CHECK_EXISTING_SESSION, { email });
    return response.data;
  } catch (error) {
    throw error;
  }
};

const forceLogoutAllDevices = async (email) => {
  try {
    const response = await api.post(API_ENDPOINTS.AUTH.FORCE_LOGOUT_ALL_DEVICES, { email });
    return response.data;
  } catch (error) {
    throw error;
  }
};

const authService = {
  login,
  register,
  logout,
  refreshToken,
  isAuthenticated,
  getUserFromStorage,
  getRefreshToken,
  clearTokens,
  getTokenFromCookie,
  setTokens,
  checkExistingSession,
  forceLogoutAllDevices,
  getUserDetails, 
};

export default authService;
